import { EPricingStatus } from '../types/enums/Pricing/EPricingStatus';
import { ICallOutboundPricing } from '../types/interfaces/Admins/ICallOutboundPricing';

/** A per-minute price the grid can sell: positive, at most three decimals. */
export const isValidOutboundPrice = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 && Number(value.toFixed(3)) === value;

/**
 * Countries the grid cannot be saved with: an extra status needs its two prices, they are what
 * the backend bills. Returns their labels, empty when the grid is fine.
 */
export const invalidOutboundPricingCountries = (pricing: ICallOutboundPricing | undefined | null): string[] => {
  if (!pricing?.continents) return [];
  return pricing.continents
    .flatMap((continent) => continent.countries ?? [])
    .filter((country) => country.status === EPricingStatus.EXTRA && !(isValidOutboundPrice(country.prices?.landline) && isValidOutboundPrice(country.prices?.cellular)))
    .map((country) => country.label || country.key);
};

/** What the grid sells, as one string: a change of status or price changes it, a relabel does not. */
export const outboundPricingSignature = (pricing: ICallOutboundPricing | undefined | null): string =>
  (pricing?.continents ?? [])
    .flatMap((continent) => continent.countries ?? [])
    .map((country) => `${country.key}:${country.status}:${country.prices?.landline ?? ''}:${country.prices?.cellular ?? ''}`)
    .sort()
    .join('|');

// The backend prices every call with the same grid object until the next config refresh
const versionCache = new WeakMap<ICallOutboundPricing, string>();

/**
 * Short id of what the grid sells: the same on each call it priced and on its history entry in the
 * backoffice, so a call leads to the grid version that priced it. FNV-1a 32 bits of the signature.
 */
export const outboundPricingVersion = (pricing: ICallOutboundPricing | undefined | null): string => {
  const cached = pricing ? versionCache.get(pricing) : undefined;
  if (cached) return cached;

  const signature = outboundPricingSignature(pricing);
  let hash = 0x811c9dc5;
  for (let i = 0; i < signature.length; i++) {
    hash ^= signature.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  const version = hash.toString(16).padStart(8, '0');
  if (pricing) versionCache.set(pricing, version);
  return version;
};